import { ProductName } from '@pypestream/api-services';

import { toolsByGraphqlName } from '../products/registry';
import { CommonProduct, GetProductsProps } from '../universal-nav/types';
import { generateBaseURLsToTools } from './generate-base-urls/generate-base-urls';

interface GetProducts {
  (props: GetProductsProps): CommonProduct[];
}

/**
 * Throws rather than falling back, so a tool reaching the nav without a logo
 * asset fails here instead of rendering a blank or borrowed one.
 */
const navTool = (name: ProductName) => {
  const tool = toolsByGraphqlName.get(name);

  if (!tool?.logo) {
    throw new Error(`getProducts: ${name} has no registered logo`);
  }

  return { label: tool.label, logo: tool.logo };
};

export const getProducts: GetProducts = ({
  project,
  allProducts,
  assignedRoles,
}) => {
  const { id: projectId } = project;

  const productIds = allProducts.reduce(
    (acc, { name, id }) => ({ ...acc, [`${name}`]: id }),
    {} as Record<ProductName, string>
  );

  const projectProducts =
    project?.projectProductSettings?.map(({ productId }) => productId) || [];

  const hasAgentAssistRoles = !!assignedRoles.find(
    (assignedRole) => assignedRole.product.name === ProductName.AgentAssist
  );

  const hasAnalyticsRoles = !!assignedRoles.find(
    (assignedRole) => assignedRole.product.name === ProductName.Analytics
  );
  const hasProStudioRoles = !!assignedRoles.find(
    (assignedRole) => assignedRole.product.name === ProductName.ProStudio
  );
  const hasMaestroStudioRoles = !!assignedRoles.find(
    (assignedRole) => assignedRole.product.name === ProductName.MaestroStudio
  );

  const productBaseURLs = generateBaseURLsToTools(window.location);
  const managerURL = window.location.host.includes('manager')
    ? `/organization/${project.accountId}/projects/${projectId}`
    : `${productBaseURLs.Manager}/organization/${project.accountId}/projects/${projectId}`;
  const contactCenterUrl =
    hasAgentAssistRoles && projectId
      ? `${productBaseURLs.ContactCenter}/app/accounts/${projectId}/dashboard`
      : undefined;
  const analyticsURL = `${productBaseURLs.Analytics}/api/projects/${projectId}/change`;
  const proStudioURL = `${productBaseURLs.ProStudio}/${projectId}`;
  const maestroStudioURL = `${productBaseURLs.Maestro}/${projectId}`;

  const commonProductsProps: CommonProduct[] = [
    {
      name: ProductName.Organization,
      ...navTool(ProductName.Organization),
      url: managerURL,
      disabled: false,
      // It means Manager is always available.
      integrated: true,
      index: 1,
    },
    {
      name: ProductName.AgentAssist,
      ...navTool(ProductName.AgentAssist),
      url: contactCenterUrl,
      conditional: true,
      disabled: !hasAgentAssistRoles,
      index: 2,
    },
    {
      name: ProductName.Analytics,
      ...navTool(ProductName.Analytics),
      // @todo: generate correct link for Analytics when service will be ready
      url: analyticsURL,
      conditional: true,
      disabled: !hasAnalyticsRoles,
      index: 3,
    },
    {
      name: ProductName.ProStudio,
      ...navTool(ProductName.ProStudio),
      url: proStudioURL,
      conditional: true,
      disabled: !hasProStudioRoles,
      index: 4,
    },
    {
      name: ProductName.MaestroStudio,
      ...navTool(ProductName.MaestroStudio),
      url: maestroStudioURL,
      conditional: true,
      disabled: !hasMaestroStudioRoles,
      index: 5,
    },
  ];
  const products: CommonProduct[] = commonProductsProps
    .map((cProduct) => {
      const productId = cProduct.name ? productIds?.[cProduct.name] || '' : '';
      const integrated = projectId
        ? projectProducts?.includes(productId)
        : false;
      const isDisabled = cProduct.conditional
        ? cProduct.disabled || !integrated
        : cProduct.disabled;

      return {
        ...cProduct,
        id: productId,
        disabled: isDisabled && cProduct.name !== ProductName.Organization,
        integrated: cProduct.integrated || integrated,
      };
    })
    .filter((cProduct) => cProduct.integrated);

  return products;
};
